import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import { headline2, headline3 } from "@shared/styles";
import Toggle from "@shared/v2/Toggle";
import { HouseUserSolidV6, Info } from "@shared/v2/Icomoon";
import Tooltip from "@shared/v2/Tooltip";
import { tryCatchHandlr } from "@shared/Utilities";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import Title from "../ModalSections/Title";
import DefaultLinks from "../ModalSections/DefaultLinks";
import CustomLinks from "../ModalSections/CustomLinks";

import { containerView, borderHr } from "./styles";
import AboutMe from "../ModalSections/AboutMe";
import PhoneNumberDropdown from "./PhoneNumberDropdown";
import { useIsAdmin } from "../../reducers/layoutReducer/selectors";
import { useContactPerson } from "../../reducers/contactsReducer/selectors";
import { setPersonValue } from "../../reducers/contactsReducer";
import LicenseNumber from "../ModalSections/LicenseNumber";

const WebsiteInfoViewRef = ({ isEditing }) => {
  const isAdmin = useIsAdmin();
  const dispatch = useDispatch();
  const person = useContactPerson();
  const { subdomain } = person.loginUserAttributes;

  // TODO: work on getting user's domain
  useEffect(() => {
    if (!person?.loginUserAttributes?.id) return;

    (async () => {
      const [res] = await tryCatchHandlr(
        caseTransformingAxios.get(`/account/${person?.loginUserAttributes?.id}/idx_configuration.json`, {
          maxRedirects: 0,
        }),
      );

      dispatch(
        setPersonValue({
          key: "domain",
          value: res.data?.data?.domain,
        }),
      );
    })();
  }, [person?.loginUserAttributes?.id]);

  return (
    <div className={containerView}>
      <div className={`tw-text-neutral-gray-75 ${headline2}`}>Website Info</div>
      {!isEditing ? (
        <div className="tw-flex tw-items-center tw-gap-16px tw-text-[#2454B2] tw-bg-[#E6EDFA] tw-border-solid tw-border-1px tw-border-[#C8D9FA] tw-py-12px tw-px-16px tw-rounded-4px">
          <HouseUserSolidV6 size="m" />
          <div className="tw-flex tw-flex-col tw-gap-4px">
            <b>Finish adding this user to unlock website settings</b>
            <span>You will be able to add a bio, contact info, title, and more!</span>
          </div>
        </div>
      ) : (
        <div className={containerView}>
          <div className="tw-text-14d">
            Unlock more fields by visiting the Roster Tab in the website builder! Note that edits made to
            users in Brivity will overwrite customizations in the Roster.{" "}
            <a
              href="https://www.brivityknowledge.com/docs/add-teammates-to-a-brivity-website"
              target="_blank"
              rel="noreferrer"
              className="tw-text-theme-text-button-color"
            >
              Click here
            </a>{" "}
            to learn more!
          </div>
          <div className={headline3}>Basic</div>
          <div className="tw-flex tw-gap-24px">
            <Toggle
              id="webroster_toggle"
              checked={person.loginUserAttributes?.showOnWebsiteRoster}
              onChange={() =>
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.showOnWebsiteRoster",
                    value: !person.loginUserAttributes?.showOnWebsiteRoster,
                  }),
                )
              }
              text="Display on roster"
            />
            <Toggle
              id="webprofile_toggle"
              checked={person.loginUserAttributes?.showAgentProfilePage}
              onChange={() =>
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.showAgentProfilePage",
                    value: !person.loginUserAttributes?.showAgentProfilePage,
                  }),
                )
              }
              text="Profile page"
            />
          </div>
          <Title />
          <LicenseNumber />
          <hr className={`${borderHr} tw-m-0`} />
          <div className={headline3}>Contact Information</div>
          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <div className="tw-flex tw-text-14d tw-font-semibold tw-text-gray-50">
              <div className="tw-mr-auto tw-w-[352px] tw-flex tw-items-center tw-gap-[4px]">
                <span>Phone Number</span>
                <Tooltip
                  content={
                    <div className="tw-w-[180px] tw-text-left">
                      Phone numbers selected here are displayed on your Brivity website and subdomain.
                      Removing a number here does not remove it from your CRM account.
                    </div>
                  }
                  trigger={<Info size="m" className="tw-text-gray-75" />}
                  placement="top"
                />
              </div>
              <div className="tw-mr-[24px] tw-w-[86px]">Displayed As</div>
              <div className="tw-flex tw-gap-[4px] tw-w-[66px]">
                <div className="tw-text-14d tw-font-semibold">Default</div>
                <Tooltip
                  content={
                    <div className="tw-w-[180px] tw-text-left">
                      Your main phone number. Shown in areas of your website where only one number is set to
                      display such as the property details page.
                    </div>
                  }
                  trigger={<Info size="m" className="tw-text-gray-75" />}
                  placement="top"
                />
              </div>
            </div>
            <PhoneNumberDropdown
              phoneNumberObj={person.loginUserAttributes.websitePhoneNumbers?.mobile}
              checked={person.loginUserAttributes.websitePhoneNumbers?.mobile?.default}
              displayedAs="Cell"
              onCheck={() => {
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.websitePhoneNumbers.office",
                    value: {
                      ...person.loginUserAttributes.websitePhoneNumbers?.office,
                      default: false,
                    },
                  }),
                );
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.websitePhoneNumbers.mobile",
                    value: {
                      ...person.loginUserAttributes.websitePhoneNumbers?.mobile,
                      default: !person.loginUserAttributes.websitePhoneNumbers?.mobile?.default,
                    },
                  }),
                );
              }}
              onTrash={() => {
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.websitePhoneNumbers.mobile",
                    value: null,
                  }),
                );

                if (person.loginUserAttributes?.websitePhoneNumbers?.office) {
                  dispatch(
                    setPersonValue({
                      key: "loginUserAttributes.websitePhoneNumbers.office",
                      value: {
                        ...person.loginUserAttributes.websitePhoneNumbers?.office,
                        default: true,
                      },
                    }),
                  );
                }
              }}
              onChange={(p) => {
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.websitePhoneNumbers.mobile",
                    value: p
                      ? {
                          default:
                            !person.loginUserAttributes?.websitePhoneNumbers?.office ||
                            person.loginUserAttributes?.websitePhoneNumbers?.mobile?.default,
                          phoneNumber: p?.value,
                          id: p?.id,
                        }
                      : null,
                  }),
                );
              }}
              disabledNumber={person.loginUserAttributes.websitePhoneNumbers?.office?.phoneNumber}
            />
            <PhoneNumberDropdown
              phoneNumberObj={person.loginUserAttributes.websitePhoneNumbers?.office}
              checked={person.loginUserAttributes.websitePhoneNumbers?.office?.default}
              displayedAs="Office"
              onCheck={() => {
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.websitePhoneNumbers.office",
                    value: {
                      ...person.loginUserAttributes.websitePhoneNumbers?.office,
                      default: !person.loginUserAttributes.websitePhoneNumbers?.office?.default,
                    },
                  }),
                );
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.websitePhoneNumbers.mobile",
                    value: {
                      ...person.loginUserAttributes.websitePhoneNumbers?.mobile,
                      default: false,
                    },
                  }),
                );
              }}
              onTrash={() => {
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.websitePhoneNumbers.office",
                    value: null,
                  }),
                );

                if (person.loginUserAttributes?.websitePhoneNumbers?.mobile) {
                  dispatch(
                    setPersonValue({
                      key: "loginUserAttributes.websitePhoneNumbers.mobile",
                      value: {
                        ...person.loginUserAttributes.websitePhoneNumbers?.mobile,
                        default: true,
                      },
                    }),
                  );
                }
              }}
              onChange={(p) =>
                dispatch(
                  setPersonValue({
                    key: "loginUserAttributes.websitePhoneNumbers.office",
                    value: p
                      ? {
                          default:
                            !person.loginUserAttributes?.websitePhoneNumbers?.mobile ||
                            person?.loginUserAttributes?.websitePhoneNumbers?.office?.default,
                          phoneNumber: p?.value,
                          id: p?.id,
                        }
                      : null,
                  }),
                )
              }
              disabledNumber={person.loginUserAttributes.websitePhoneNumbers?.mobile?.phoneNumber}
            />
          </div>
          <hr className={`${borderHr} tw-m-0`} />
          {/* NOTE: this will be reintroduced later */}
          {/* 
          <div className={headline3}>Subdomain</div>
          <div className="tw-text-gray-50 tw-text-14d">
            <span className={isAdmin ? "tw-hidden" : ""}>
              Your subdomain will be identical to the account owner’s website except their personal
              information is replaced with yours (where applicable).
            </span>{" "}
            Leads generated on your subdomain will be automatically routed to you. Limit: 1 per Brivity user.
          </div>
          {(!subdomain || subdomain?.trim().length === 0) && (
            <>
              <div className="">You do not currently have a subdomain.</div>
              <Button
                schema="secondary"
                className="tw-w-[98px] tw-flex tw-items-center tw-whitespace-nowrap"
                onClick={onSubdomainOpen}
              >
                <PlusSolidV6 size="s" className="tw-mr-[6px]" />
                <span>SUBDOMAIN</span>
              </Button>
            </>
          )}
          {websiteURL && (
            <div className="tw-p-8px tw-flex tw-justify-between tw-border-1px tw-border-solid tw-border-gray-100 tw-border-gray-10 tw-rounded-4px">
              <a
                className="tw-text-theme-text-button-color tw-text-14d tw-flex tw-items-center tw-justify-center tw-gap-6px"
                href={`http://www.${websiteURL}`}
                target="_blank"
                rel="noreferrer"
              >
                <span>{websiteURL}</span>
                <ArrowUpRightFromSquareSolidV6 size="m" />
              </a>
              <div className="tw-flex tw-gap-8px">
                {showCopy && (
                  <div className="tw-flex tw-gap-4px tw-items-center tw-text-green-100">
                    <CheckSolidV6 size="m" />
                    <span>Copied!</span>
                  </div>
                )}
                <Button
                  className="!tw-w-24px tw-flex tw-items-center tw-justify-center"
                  size="small"
                  schema="tertiary"
                  onClick={() => {
                    navigator.clipboard.writeText(websiteURL);
                    setShowCopy(true);
                    setTimeout(() => {
                      setShowCopy(false);
                    }, 2000);
                  }}
                >
                  <CopySolidV6 size="m" />
                </Button>
                <Button
                  className="!tw-w-24px tw-flex tw-items-center tw-justify-center"
                  size="small"
                  schema="tertiary"
                  onClick={onSubdomainEdit}
                >
                  <PencilSolidV6 size="m" />
                </Button>
                <Button
                  className="!tw-w-24px tw-flex tw-items-center tw-justify-center"
                  size="small"
                  schema="tertiary"
                  onClick={onSubdomainRemove}
                >
                  <TrashCanSolidV6 size="m" />
                </Button>
              </div>
            </div>
          )}
         */}
          <hr className={`${borderHr} tw-m-0`} />
          {(isAdmin || subdomain) && (
            <>
              <div className={headline3}>Review Links</div>
              <div className="tw-text-gray-50">
                Enter the links for the websites you use to collect client reviews. They are added to your{" "}
                <b>Links</b> page. (Example: john.mywebsite.com/links)
              </div>
              <DefaultLinks />
              <hr className={`${borderHr} tw-m-0`} />
              <div className={headline3}>Custom Links</div>
              <div className="tw-text-gray-50 tw-tracking-[-0.4px]">
                Custom links are added to your <b>Links</b> page. (Example: john.mywebsite.com/links)
              </div>
              <CustomLinks />
              <hr className={`${borderHr} tw-m-0`} />
            </>
          )}
          <div className={headline3}>About Me</div>
          <AboutMe />
        </div>
      )}
    </div>
  );
};

export default WebsiteInfoViewRef;

WebsiteInfoViewRef.propTypes = {
  isEditing: PropTypes.bool,
};

WebsiteInfoViewRef.defaultProps = {
  isEditing: false,
};
